import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, unref as _unref, isRef as _isRef, vModelText as _vModelText, withDirectives as _withDirectives, createVNode as _createVNode, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "sclaims" }
const _hoisted_2 = {
  class: "sclaims__kpis",
  "data-tour": "seller-claims-kpis"
}
const _hoisted_3 = { class: "sclaims__kpi-label" }
const _hoisted_4 = { class: "sclaims__kpi-value" }
const _hoisted_5 = { class: "sclaims__kpi-desc" }
const _hoisted_6 = { class: "sui-card sui-card--flush" }
const _hoisted_7 = {
  class: "sui-tabs",
  "data-tour": "seller-claims-tabs"
}
const _hoisted_8 = ["onClick"]
const _hoisted_9 = {
  class: "sui-toolbar sclaims__toolbar",
  "data-tour": "seller-claims-toolbar"
}
const _hoisted_10 = {
  class: "sui-presets",
  role: "group",
  "aria-label": "기간"
}
const _hoisted_11 = ["onClick"]
const _hoisted_12 = { class: "sui-date" }
const _hoisted_13 = { class: "sui-date" }
const _hoisted_14 = { class: "sui-search" }
const _hoisted_15 = ["disabled"]
const _hoisted_16 = {
  key: 0,
  class: "sclaims__error"
}
const _hoisted_17 = {
  key: 1,
  class: "sclaims__list",
  "data-tour": "seller-claims-list"
}
const _hoisted_18 = { class: "sclaims__item-head" }
const _hoisted_19 = { class: "sclaims__order-id" }
const _hoisted_20 = { class: "sclaims__date" }
const _hoisted_21 = { class: "sclaims__item-body" }
const _hoisted_22 = ["src", "alt"]
const _hoisted_23 = { class: "sclaims__info" }
const _hoisted_24 = { class: "sclaims__name" }
const _hoisted_25 = { class: "sclaims__meta" }
const _hoisted_26 = {
  key: 0,
  class: "sclaims__reason"
}
const _hoisted_27 = { class: "sclaims__reason" }
const _hoisted_28 = {
  key: 1,
  class: "sclaims__reason is-reject"
}
const _hoisted_29 = { class: "sclaims__amount" }
const _hoisted_30 = { class: "sclaims__amount-block" }
const _hoisted_31 = { class: "sclaims__amount-block is-settle" }
const _hoisted_32 = {
  key: 0,
  class: "sclaims__actions"
}
const _hoisted_33 = ["disabled", "onClick"]
const _hoisted_34 = ["disabled", "onClick"]
const _hoisted_35 = { class: "sclaims__reject-actions" }
const _hoisted_36 = ["disabled"]
const _hoisted_37 = {
  key: 2,
  class: "sclaims__done"
}
const _hoisted_38 = {
  key: 3,
  class: "sclaims__done"
}
const _hoisted_39 = {
  key: 2,
  class: "sui-empty"
}
const _hoisted_40 = {
  key: 0,
  class: "sui-toast"
}

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import { confirmDialog } from '@/composables/useConfirmDialog'
import { useListDateRange } from '@/composables/useListDateRange'
import { formatWon } from '@/constants/sellerData'
import {
  MALL_ORDER_ITEM_STATUS_LABEL,
  mallPayMethodLabel,
  approveSellerClaim,
  fetchSellerClaims,
  rejectSellerClaim,
  type MallOrderItemStatus,
  type SellerClaimItem,
} from '@/api/mallOrder'

type TabKey = 'pending' | 'CANCEL_REQUEST' | 'RETURN_REQUEST' | 'CANCELED' | 'REJECTED'


export default /*@__PURE__*/_defineComponent({
  __name: 'SellerClaimsPage',
  setup(__props) {

const TAB_KEYS: TabKey[] = [
  'pending',
  'CANCEL_REQUEST',
  'RETURN_REQUEST',
  'CANCELED',
  'REJECTED',
]

const route = useRoute()
const router = useRouter()
const items = ref<SellerClaimItem[]>([])
const loading = ref(false)
const errorMessage = ref('')
const working = ref(0)
const toast = ref('')
let toastTimer: ReturnType<typeof setTimeout> | undefined
const rejectTarget = ref<SellerClaimItem | null>(null)
const rejectReason = ref('')

function tabFromQuery(): TabKey {
  const raw = String(route.query.type || route.query.tab || '')
  if (TAB_KEYS.includes(raw as TabKey)) return raw as TabKey
  return 'pending'
}

const activeTab = ref<TabKey>(tabFromQuery())
const keyword = ref(String(route.query.keyword ?? '').trim())
const { fromDate, toDate, preset, presets, applyPreset, onManualDateChange, inRange } =
  useListDateRange('3m')

function setActiveTab(next: TabKey) {
  activeTab.value = next
  const query: Record<string, string> = {}
  if (next !== 'pending') query.type = next
  if (keyword.value.trim()) query.keyword = keyword.value.trim()
  router.replace({
    path: '/seller/claims',
    query,
  })
}

watch(
  () => [route.query.type, route.query.tab, route.query.keyword],
  () => {
    activeTab.value = tabFromQuery()
    keyword.value = String(route.query.keyword ?? '').trim()
  },
)

function isPending(status: MallOrderItemStatus) {
  return status === 'CANCEL_REQUEST' || status === 'RETURN_REQUEST'
}

function isRejected(item: SellerClaimItem) {
  return Boolean(item.claimRejectReason) && !isPending(item.status) && item.status !== 'CANCELED'
}

function statusTone(status: MallOrderItemStatus): string {
  if (status === 'CANCEL_REQUEST') return 'sui-badge--amber'
  if (status === 'RETURN_REQUEST') return 'sui-badge--red'
  return 'sui-badge--gray'
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const result = await fetchSellerClaims()
    items.value = result.items
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '목록을 불러오지 못했습니다.'
    items.value = []
  } finally {
    loading.value = false
  }
}

onMounted(load)

const pendingList = computed(() => items.value.filter((item) => isPending(item.status)))

const rejectedList = computed(() => items.value.filter((item) => isRejected(item)))

const tabs = computed(() => [
  { key: 'pending' as TabKey, label: '처리 대기', count: pendingList.value.length },
  {
    key: 'CANCEL_REQUEST' as TabKey,
    label: '취소 요청',
    count: items.value.filter((item) => item.status === 'CANCEL_REQUEST').length,
  },
  {
    key: 'RETURN_REQUEST' as TabKey,
    label: '반품 요청',
    count: items.value.filter((item) => item.status === 'RETURN_REQUEST').length,
  },
  {
    key: 'CANCELED' as TabKey,
    label: '승인 완료',
    count: items.value.filter((item) => item.status === 'CANCELED').length,
  },
  {
    key: 'REJECTED' as TabKey,
    label: '반려',
    count: rejectedList.value.length,
  },
])

const filtered = computed(() => {
  const query = keyword.value.toLowerCase()

  return items.value
    .filter((item) => inRange(item.claimAt || item.claimResolvedAt || item.orderedAt))
    .filter((item) => {
      if (activeTab.value === 'pending') return isPending(item.status)
      if (activeTab.value === 'REJECTED') return isRejected(item)
      if (activeTab.value === 'CANCELED') return item.status === 'CANCELED'
      return item.status === activeTab.value
    })
    .filter((item) => {
      if (!query) return true
      return (
        item.orderCode.toLowerCase().includes(query) ||
        item.name.toLowerCase().includes(query) ||
        item.buyerOrg.toLowerCase().includes(query)
      )
    })
})

const summary = computed(() => {
  const pendingAmount = pendingList.value.reduce((sum, item) => sum + item.amount, 0)
  const done = items.value.filter((item) => item.status === 'CANCELED').length
  const total = items.value.length
  const rate = total ? ((done / total) * 100).toFixed(1) : '0.0'

  return [
    {
      label: '처리 대기',
      value: String(pendingList.value.length),
      unit: '건',
      desc: '승인 또는 반려가 필요합니다',
    },
    {
      label: '환불 예정 금액',
      value: formatWon(pendingAmount),
      unit: '원',
      desc: '승인 시 정산에서 차감됩니다',
    },
    {
      label: '처리 완료 비율',
      value: rate,
      unit: '%',
      desc: '전체 클레임 대비',
    },
    {
      label: '처리 완료',
      value: String(done),
      unit: '건',
      desc: '승인된 취소 · 반품',
    },
  ]
})

function notify(message: string) {
  toast.value = message
  window.clearTimeout(toastTimer)
  toastTimer = setTimeout(() => {
    toast.value = ''
  }, 2400)
}

function sellerSettleCaption(item: SellerClaimItem) {
  if (item.claimType !== 'RETURN' && item.status !== 'RETURN_REQUEST') return '내 정산 차감'
  if (item.returnCause === 'CHANGE') return '받을 반품비'
  return '상품 정산'
}

function sellerSettleAmount(item: SellerClaimItem) {
  if (item.claimType === 'RETURN' || item.status === 'RETURN_REQUEST') {
    if (item.returnCause === 'CHANGE') return Math.max(0, Number(item.appliedReturnFee) || 0)
    return 0
  }
  return item.settlement
}

async function onApprove(item: SellerClaimItem) {
  if (working.value) return
  const isReturn = item.status === 'RETURN_REQUEST'
  const card = item.payMethod === 'TOSS'
  const changeMind = item.returnCause === 'CHANGE'
  const ok = await confirmDialog({
    title: '클레임 승인',
    message: isReturn
      ? changeMind
        ? '단순 변심 반품을 승인할까요? 상품 대금은 정산되지 않고, 반품비만 정산으로 받습니다. 재고는 자동으로 돌아오지 않습니다.'
        : card
          ? '하자·오배송 반품을 승인할까요? 상품 대금은 정산되지 않고, 결제했던 카드·간편결제로 전액 환불됩니다. 재고는 자동으로 돌아오지 않습니다.'
          : '하자·오배송 반품을 승인할까요? 상품 대금은 정산되지 않습니다. 재고는 자동으로 돌아오지 않습니다.'
      : card
        ? '요청을 승인할까요? 결제했던 카드·간편결제로 환불됩니다.'
        : '요청을 승인할까요? 승인하면 정산에서 차감됩니다.',
    detail: [
      item.orderCode,
      item.name,
      isReturn
        ? changeMind
          ? `받을 반품비 ${formatWon(sellerSettleAmount(item))}원`
          : '상품 정산 없음'
        : `정산 차감 ${formatWon(item.settlement)}원`,
      isReturn
        ? `실물 입고 후 상품관리에서 재고를 추가해 주세요.${item.productCode ? `\n(/seller/products?code=${item.productCode})` : ''}`
        : '',
    ]
      .filter(Boolean)
      .join('\n'),
    confirmLabel: '승인',
    tone: 'primary',
  })
  if (!ok) return

  working.value = item.idx
  try {
    const message = await approveSellerClaim(item.idx)
    item.status = 'CANCELED'
    item.claimResolvedAt = new Date().toISOString()
    notify(
      isReturn
        ? `${message} 재고는 상품관리에서 수동으로 반영해 주세요.`
        : message,
    )
  } catch (error) {
    notify(error instanceof Error ? error.message : '승인에 실패했습니다.')
  } finally {
    working.value = 0
  }
}

function openReject(item: SellerClaimItem) {
  rejectTarget.value = item
  rejectReason.value = ''
}

function closeReject() {
  rejectTarget.value = null
  rejectReason.value = ''
}

async function submitReject() {
  if (!rejectTarget.value || working.value || !rejectReason.value) return
  const ok = await confirmDialog({
    title: '클레임 반려',
    message: '이 요청을 반려할까요? 사유가 구매 기관에 전달됩니다.',
    detail: `${rejectTarget.value.orderCode}\n${rejectTarget.value.name}\n사유: ${rejectReason.value}`,
    confirmLabel: '반려',
    tone: 'danger',
  })
  if (!ok) return

  working.value = rejectTarget.value.idx
  try {
    const message = await rejectSellerClaim(rejectTarget.value.idx, rejectReason.value)
    closeReject()
    notify(message)
    await load()
    setActiveTab('REJECTED')
  } catch (error) {
    notify(error instanceof Error ? error.message : '반려에 실패했습니다.')
  } finally {
    working.value = 0
  }
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')}`
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(summary.value, (card) => {
        return (_openBlock(), _createElementBlock("article", {
          key: card.label,
          class: "sui-card sclaims__kpi"
        }, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(card.label), 1),
          _createElementVNode("p", _hoisted_4, [
            _createTextVNode(_toDisplayString(card.value), 1),
            _createElementVNode("em", null, _toDisplayString(card.unit), 1)
          ]),
          _createElementVNode("p", _hoisted_5, _toDisplayString(card.desc), 1)
        ]))
      }), 128))
    ]),
    _createElementVNode("section", _hoisted_6, [
      _createElementVNode("div", _hoisted_7, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
          return (_openBlock(), _createElementBlock("button", {
            key: tab.key,
            type: "button",
            class: _normalizeClass(["sui-tab", { 'is-active': tab.key === activeTab.value }]),
            onClick: ($event: any) => (setActiveTab(tab.key))
          }, [
            _createTextVNode(_toDisplayString(tab.label) + " ", 1),
            _createElementVNode("em", null, _toDisplayString(tab.count), 1)
          ], 10, _hoisted_8))
        }), 128))
      ]),
      _createElementVNode("div", _hoisted_9, [
        _createElementVNode("div", _hoisted_10, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(presets), (item) => {
            return (_openBlock(), _createElementBlock("button", {
              key: item.key,
              type: "button",
              class: _normalizeClass({ 'is-active': _unref(preset) === item.key }),
              onClick: ($event: any) => (_unref(applyPreset)(item.key))
            }, _toDisplayString(item.label), 11, _hoisted_11))
          }), 128))
        ]),
        _createElementVNode("label", _hoisted_12, [
          _cache[6] || (_cache[6] = _createElementVNode("span", null, "시작일", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(fromDate) ? (fromDate).value = $event : null)),
            type: "date",
            onChange: _cache[1] || (_cache[1] = 
//@ts-ignore
(...args) => (_unref(onManualDateChange) && _unref(onManualDateChange)(...args)))
          }, null, 544), [
            [_vModelText, _unref(fromDate)]
          ])
        ]),
        _createElementVNode("label", _hoisted_13, [
          _cache[7] || (_cache[7] = _createElementVNode("span", null, "종료일", -1)),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_isRef(toDate) ? (toDate).value = $event : null)),
            type: "date",
            onChange: _cache[3] || (_cache[3] = 
//@ts-ignore
(...args) => (_unref(onManualDateChange) && _unref(onManualDateChange)(...args)))
          }, null, 544), [
            [_vModelText, _unref(toDate)]
          ])
        ]),
        _createElementVNode("label", _hoisted_14, [
          _createVNode(AppIcon, {
            icon: "mdi-magnify",
            size: 17
          }),
          _withDirectives(_createElementVNode("input", {
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((keyword).value = $event)),
            type: "search",
            placeholder: "주문번호 · 상품명 · 기관명 검색"
          }, null, 512), [
            [
              _vModelText,
              keyword.value,
              void 0,
              { trim: true }
            ]
          ])
        ]),
        _cache[9] || (_cache[9] = _createElementVNode("span", { class: "sui-toolbar__spacer" }, null, -1)),
        _createElementVNode("button", {
          type: "button",
          class: "sui-btn",
          disabled: loading.value,
          onClick: load
        }, [
          _createVNode(AppIcon, {
            icon: "mdi-refresh",
            size: 16
          }),
          _cache[8] || (_cache[8] = _createTextVNode(" 새로고침 ", -1))
        ], 8, _hoisted_15)
      ]),
      (errorMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_16, [
            _createVNode(AppIcon, {
              icon: "mdi-alert-outline",
              size: 16
            }),
            _createTextVNode(" " + _toDisplayString(errorMessage.value), 1)
          ]))
        : _createCommentVNode("", true),
      (filtered.value.length)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_17, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filtered.value, (item) => {
              return (_openBlock(), _createElementBlock("li", {
                key: item.idx,
                class: "sclaims__item"
              }, [
                _createElementVNode("div", _hoisted_18, [
                  _createElementVNode("span", {
                    class: _normalizeClass(["sui-badge", isRejected(item) ? 'sui-badge--gray' : statusTone(item.status)])
                  }, _toDisplayString(isRejected(item) ? '반려' : _unref(MALL_ORDER_ITEM_STATUS_LABEL)[item.status]), 3),
                  _createElementVNode("span", _hoisted_19, _toDisplayString(item.orderCode), 1),
                  _createElementVNode("span", {
                    class: _normalizeClass(["sclaims__pay", { 'is-card': item.payMethod === 'TOSS' }])
                  }, _toDisplayString(_unref(mallPayMethodLabel)(item)), 3),
                  _createElementVNode("span", _hoisted_20, _toDisplayString(formatDate(item.claimAt || item.orderedAt)) + " 요청", 1)
                ]),
                _createElementVNode("div", _hoisted_21, [
                  (item.image)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        class: "sclaims__img",
                        src: item.image,
                        alt: item.name,
                        loading: "lazy"
                      }, null, 8, _hoisted_22))
                    : _createCommentVNode("", true),
                  _createElementVNode("div", _hoisted_23, [
                    _createElementVNode("p", _hoisted_24, _toDisplayString(item.name), 1),
                    _createElementVNode("p", _hoisted_25, [
                      _createTextVNode(_toDisplayString(item.buyerOrg) + " ", 1),
                      (item.buyerName)
                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                            _createTextVNode(" · " + _toDisplayString(item.buyerName), 1)
                          ], 64))
                        : _createCommentVNode("", true),
                      _createTextVNode(" · " + _toDisplayString(item.qty) + "개 ", 1)
                    ]),
                    (item.claimType === 'RETURN' && item.returnCause)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_26, _toDisplayString(item.returnCause === 'CHANGE' ? '단순 변심' : '상품 하자·오배송'), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("p", _hoisted_27, [
                      _createVNode(AppIcon, {
                        icon: "mdi-message-alert-outline",
                        size: 15
                      }),
                      _createTextVNode(" " + _toDisplayString(item.claimReason || '사유 없음'), 1)
                    ]),
                    (isRejected(item))
                      ? (_openBlock(), _createElementBlock("p", _hoisted_28, [
                          _createVNode(AppIcon, {
                            icon: "mdi-close-circle-outline",
                            size: 15
                          }),
                          _createTextVNode(" 반려 · " + _toDisplayString(item.claimRejectReason), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_29, [
                    _createElementVNode("div", _hoisted_30, [
                      _cache[10] || (_cache[10] = _createElementVNode("span", null, "구매자 환불액", -1)),
                      _createElementVNode("strong", null, _toDisplayString(_unref(formatWon)(item.refundAmount || item.amount)) + "원", 1)
                    ]),
                    _createElementVNode("div", _hoisted_31, [
                      _createElementVNode("span", null, _toDisplayString(sellerSettleCaption(item)), 1),
                      _createElementVNode("em", null, _toDisplayString(_unref(formatWon)(sellerSettleAmount(item))) + "원", 1)
                    ])
                  ])
                ]),
                (isPending(item.status))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_32, [
                      _createElementVNode("button", {
                        type: "button",
                        class: "sui-btn sui-btn--sm",
                        disabled: working.value === item.idx,
                        onClick: ($event: any) => (openReject(item))
                      }, " 요청 반려 ", 8, _hoisted_33),
                      _createElementVNode("button", {
                        type: "button",
                        class: "sui-btn sui-btn--sm sui-btn--primary",
                        disabled: working.value === item.idx,
                        onClick: ($event: any) => (onApprove(item))
                      }, _toDisplayString(working.value === item.idx ? '처리 중…' : '요청 승인'), 9, _hoisted_34)
                    ]))
                  : _createCommentVNode("", true),
                (rejectTarget.value?.idx === item.idx)
                  ? (_openBlock(), _createElementBlock("form", {
                      key: 1,
                      class: "sclaims__reject",
                      onSubmit: _withModifiers(submitReject, ["prevent"])
                    }, [
                      _createElementVNode("label", null, [
                        _cache[11] || (_cache[11] = _createElementVNode("span", null, "반려 사유", -1)),
                        _withDirectives(_createElementVNode("textarea", {
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((rejectReason).value = $event)),
                          rows: "3",
                          maxlength: "255",
                          placeholder: "구매 기관에 안내할 반려 사유를 적어 주세요."
                        }, null, 512), [
                          [
                            _vModelText,
                            rejectReason.value,
                            void 0,
                            { trim: true }
                          ]
                        ])
                      ]),
                      _createElementVNode("div", _hoisted_35, [
                        _createElementVNode("button", {
                          type: "button",
                          class: "sui-btn sui-btn--sm",
                          onClick: closeReject
                        }, "취소"),
                        _createElementVNode("button", {
                          type: "submit",
                          class: "sui-btn sui-btn--sm sui-btn--primary",
                          disabled: working.value === item.idx || !rejectReason.value
                        }, " 반려 확정 ", 8, _hoisted_36)
                      ])
                    ], 32))
                  : (isRejected(item))
                    ? (_openBlock(), _createElementBlock("p", _hoisted_37, [
                        _createVNode(AppIcon, {
                          icon: "mdi-close-circle-outline",
                          size: 15
                        }),
                        _cache[12] || (_cache[12] = _createTextVNode(" 반려된 요청입니다. ", -1)),
                        (item.claimResolvedAt)
                          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                              _createTextVNode(" · " + _toDisplayString(formatDate(item.claimResolvedAt)), 1)
                            ], 64))
                          : _createCommentVNode("", true)
                      ]))
                    : (!isPending(item.status))
                      ? (_openBlock(), _createElementBlock("p", _hoisted_38, [
                          _createVNode(AppIcon, {
                            icon: "mdi-check-circle-outline",
                            size: 15
                          }),
                          _cache[13] || (_cache[13] = _createTextVNode(" 승인 처리된 건입니다. ", -1)),
                          (item.claimResolvedAt)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createTextVNode(" · " + _toDisplayString(formatDate(item.claimResolvedAt)), 1)
                              ], 64))
                            : _createCommentVNode("", true)
                        ]))
                      : _createCommentVNode("", true)
              ]))
            }), 128))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_39, [
            _createVNode(AppIcon, {
              icon: "mdi-emoticon-happy-outline",
              size: 32
            }),
            _createElementVNode("p", null, _toDisplayString(loading.value ? '불러오는 중입니다…' : '처리할 취소 · 반품 요청이 없습니다.'), 1)
          ]))
    ]),
    _createVNode(_Transition, { name: "sui-toast" }, {
      default: _withCtx(() => [
        (toast.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_40, [
              _createVNode(AppIcon, {
                icon: "mdi-check-circle",
                size: 18
              }),
              _createTextVNode(" " + _toDisplayString(toast.value), 1)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})